import { NavLink, useParams } from 'react-router-dom'
import {
  Users, BarChart2, Settings, Building2, LayoutDashboard,
  TrendingUp, UserCheck, LogOut, ChevronRight, CalendarDays, CalendarHeart, Mail, MessageCircle, Mic, Receipt,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../store/auth'
import { authApi } from '../../api/auth'
import { waApi } from '../../api/whatsapp'
import { cn, fullName } from '../../lib/utils'

const NAV = [
  { to: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: 'leads', label: 'Leads', icon: TrendingUp },
  { to: 'deals', label: 'Deals', icon: BarChart2 },
  { to: 'invoices', label: 'Proformas', icon: Receipt },
  { to: 'contacts', label: 'Contacts', icon: Users },
  { to: 'organizations', label: 'Organizations', icon: Building2 },
  { to: 'calendar', label: 'Calendar', icon: CalendarDays },
  { to: 'appointments', label: 'Appointments', icon: CalendarHeart },
  { to: 'mail', label: 'Mail', icon: Mail },
  { to: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { to: 'recordings', label: 'Recordings', icon: Mic },
]

function WaUnreadBadge({ companyId }: { companyId: string }) {
  const { data } = useQuery({
    queryKey: ['wa-unread', companyId],
    queryFn: () => waApi.unreadCount(companyId),
    refetchInterval: 30_000,
  })
  if (!data?.total) return null
  return (
    <span className="ml-auto rounded-full bg-green-500 px-1.5 text-xs text-white">
      {data.total > 99 ? '99+' : data.total}
    </span>
  )
}

function abbreviate(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return 'CRM'
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase()
  return words.map(w => w[0]).join('').toUpperCase().slice(0, 4)
}

export function Sidebar({ companyName, logoUrl }: { companyName?: string; logoUrl?: string }) {
  const { companyId } = useParams<{ companyId: string }>()
  const { user, clearAuth } = useAuthStore()
  const homepageUrl = import.meta.env.FRONTEND_URL || window.location.origin

  const handleLogout = async () => {
    await authApi.logout().catch(() => {})
    clearAuth()
  }

  const base = `/company/${companyId}`
  const label = companyName ? abbreviate(companyName) : 'CRM'

  return (
    <div className="w-56 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full">
      <div className="px-4 py-4 border-b border-gray-200">
        <a href={homepageUrl} className="flex items-center gap-2 hover:opacity-90 transition-opacity">
          {logoUrl ? (
            <img src={logoUrl} alt={label} className="w-7 h-7 rounded object-contain" />
          ) : (
            <div className="w-7 h-7 rounded bg-primary-600 flex items-center justify-center">
              <UserCheck className="w-4 h-4 text-white" />
            </div>
          )}
          <span className="font-semibold text-gray-900 text-sm">{label}</span>
        </a>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV.filter(({ to }) => !((to === 'organizations' || to === 'recordings') && user?.role === 'sales_user')).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={`${base}/${to}`}
            className={({ isActive }) => cn('sidebar-link', isActive && 'active')}
          >
            <Icon className="w-4 h-4" />
            {label}
            {to === 'whatsapp' && companyId && <WaUnreadBadge companyId={companyId} />}
          </NavLink>
        ))}

        {(user?.role === 'company_owner' || user?.role === 'administrator') && (
          <>
            <div className="my-2 border-t border-gray-200" />
            <NavLink
              to={`${base}/settings`}
              className={({ isActive }) => cn('sidebar-link', isActive && 'active')}
            >
              <Settings className="w-4 h-4" />
              Settings
              <ChevronRight className="w-3.5 h-3.5 ml-auto" />
            </NavLink>
          </>
        )}
      </nav>

      <div className="px-2 py-3 border-t border-gray-200">
        <div className="flex items-center gap-2 px-2 py-1 mb-1">
          <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xs font-semibold">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-900 truncate">{fullName(user?.firstName, user?.lastName)}</p>
            <p className="text-xs text-gray-500 truncate">{user?.role}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700">
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  )
}
